import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { LogoMark } from './Logo';
import './auth-modal.css';

// Sign in, sign up, password reset and setting a new password (after the
// reset email's link), with Google or email. A successful sign in calls onSuccess.
// With onJoinWaitlist, new visitors are sent to the waitlist instead of sign up.
const VIEWS = {
    sign_in: { title: 'Welcome back', sub: 'Sign in to plan and write this week’s posts.', submit: 'Sign in' },
    sign_up: { title: 'Create your account', sub: 'Get your first 3 posts free. No card needed.', submit: 'Create account' },
    forgot: { title: 'Reset your password', sub: 'We’ll email you a link to set a new one.', submit: 'Send reset link' },
    update_password: { title: 'Set a new password', sub: 'Choose a password for your account.', submit: 'Save password' },
};

const MIN_PASSWORD = 8;

// Supabase's messages, in plain words
const friendlyError = (error) => {
    const message = error?.message || '';
    if (/invalid login credentials/i.test(message)) return 'That email and password don’t match. Try again, or reset your password.';
    if (/email not confirmed/i.test(message)) return 'Confirm your email first: we sent you a link when you signed up.';
    if (/already registered|already exists/i.test(message)) return 'There’s already an account with this email. Sign in instead.';
    if (/rate limit|too many/i.test(message)) return 'Too many attempts. Wait a minute and try again.';
    if (/password/i.test(message) && /(least|short|weak)/i.test(message)) return `Use at least ${MIN_PASSWORD} characters for your password.`;
    if (/network|fetch/i.test(message)) return 'Couldn’t reach the server. Check your connection and try again.';
    return message || 'Something went wrong. Please try again.';
};

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
);

const AuthModal = ({ onClose, onSuccess, redirectTo, initialView = 'sign_in', onJoinWaitlist }) => {
    const [view, setView] = useState(initialView);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [sent, setSent] = useState(null); // { title, body } once an email is on its way
    const firstField = useRef(null);

    // Keep the latest callback and view without re-subscribing on every render
    const onSuccessRef = useRef(onSuccess);
    onSuccessRef.current = onSuccess;
    const viewRef = useRef(view);
    viewRef.current = view;

    useEffect(() => {
        // Already signed in (or just signed in): move on, except while setting a new password
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session && viewRef.current !== 'update_password') onSuccessRef.current?.(session.user);
        });
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_IN' && session && viewRef.current !== 'update_password') onSuccessRef.current?.(session.user);
        });
        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        const handleKey = (event) => event.key === 'Escape' && onClose?.();
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    useEffect(() => {
        setError('');
        firstField.current?.focus();
    }, [view]);

    const switchTo = (next) => {
        setView(next);
        setSent(null);
        setPassword('');
    };

    const run = async (key, action) => {
        setBusy(key);
        setError('');
        try {
            await action();
        } catch (e) {
            setError(friendlyError(e));
        } finally {
            setBusy('');
        }
    };

    const handleGoogle = () => run('google', async () => {
        const { error: e } = await supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo } });
        if (e) throw e;
        // The browser is on its way to Google
    });

    const handleSubmit = (event) => {
        event.preventDefault();
        const cleanEmail = email.trim();
        if (view !== 'update_password' && !/^\S+@\S+\.\S+$/.test(cleanEmail)) {
            setError('Enter a valid email address.');
            return;
        }
        if ((view === 'sign_up' || view === 'update_password') && password.length < MIN_PASSWORD) {
            setError(`Use at least ${MIN_PASSWORD} characters for your password.`);
            return;
        }
        run('submit', async () => {
            if (view === 'sign_in') {
                const { data, error: e } = await supabase.auth.signInWithPassword({ email: cleanEmail, password });
                if (e) throw e;
                onSuccessRef.current?.(data.user);
            } else if (view === 'sign_up') {
                const { data, error: e } = await supabase.auth.signUp({
                    email: cleanEmail,
                    password,
                    options: { emailRedirectTo: redirectTo },
                });
                if (e) throw e;
                // Supabase returns a fake user with no identities when the email is already registered
                if (data.user && data.user.identities?.length === 0) throw new Error('User already registered');
                if (data.session) onSuccessRef.current?.(data.user);
                else setSent({ title: 'Check your email', body: `We sent a confirmation link to ${cleanEmail}. Open it to finish creating your account.` });
            } else if (view === 'forgot') {
                const { error: e } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
                    redirectTo: `${window.location.origin}/?resetPassword=true`,
                });
                if (e) throw e;
                setSent({ title: 'Check your email', body: `If there’s an account for ${cleanEmail}, a link to reset your password is on its way.` });
            } else if (view === 'update_password') {
                const { data, error: e } = await supabase.auth.updateUser({ password });
                if (e) throw e;
                onSuccessRef.current?.(data.user);
            }
        });
    };

    const copy = VIEWS[view];
    const usesPassword = view !== 'forgot';
    const usesEmail = view !== 'update_password';

    return (
        <div className="am-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
            <div className="am-card" role="dialog" aria-modal="true" aria-labelledby="am-title">
                <button type="button" className="am-close" aria-label="Close" onClick={onClose}>
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                </button>

                <div className="am-head">
                    <LogoMark size={48} alt="" />
                    <h2 id="am-title">{sent ? sent.title : copy.title}</h2>
                    <p>{sent ? sent.body : copy.sub}</p>
                </div>

                {sent ? (
                    <button type="button" className="am-primary" onClick={() => switchTo('sign_in')}>Back to sign in</button>
                ) : (
                    <>
                        {(view === 'sign_in' || view === 'sign_up') && (
                            <>
                                <button type="button" className="am-google" onClick={handleGoogle} disabled={Boolean(busy)}>
                                    {busy === 'google' ? <span className="am-spinner is-dark" /> : <GoogleIcon />}
                                    Continue with Google
                                </button>
                                <div className="am-divider"><span>or with email</span></div>
                            </>
                        )}

                        <form onSubmit={handleSubmit} noValidate className="am-form">
                            {usesEmail && (
                                <label className="am-field">
                                    <span>Email</span>
                                    <input
                                        ref={firstField}
                                        type="email"
                                        autoComplete="email"
                                        inputMode="email"
                                        placeholder="you@company.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </label>
                            )}
                            {usesPassword && (
                                <label className="am-field">
                                    <span className="am-label-row">
                                        {view === 'update_password' ? 'New password' : 'Password'}
                                        {view === 'sign_in' && (
                                            <button type="button" className="am-link" onClick={() => switchTo('forgot')}>Forgot password?</button>
                                        )}
                                    </span>
                                    <span className="am-password">
                                        <input
                                            ref={usesEmail ? undefined : firstField}
                                            type={showPassword ? 'text' : 'password'}
                                            autoComplete={view === 'sign_in' ? 'current-password' : 'new-password'}
                                            placeholder={view === 'sign_in' ? 'Your password' : `At least ${MIN_PASSWORD} characters`}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="am-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showPassword ? 'Hide' : 'Show'}
                                        </button>
                                    </span>
                                </label>
                            )}

                            {error && <div className="am-error" role="alert">{error}</div>}

                            <button type="submit" className="am-primary" disabled={Boolean(busy)}>
                                {busy === 'submit' ? <span className="am-spinner" /> : copy.submit}
                            </button>
                        </form>

                        <div className="am-switch">
                            {view === 'sign_in' && (onJoinWaitlist
                                ? <>Don’t have access yet? <button type="button" className="am-link" onClick={onJoinWaitlist}>Join the waitlist</button></>
                                : <>New to light speed? <button type="button" className="am-link" onClick={() => switchTo('sign_up')}>Create an account</button></>)}
                            {view === 'sign_up' && <>Already have an account? <button type="button" className="am-link" onClick={() => switchTo('sign_in')}>Sign in</button></>}
                            {view === 'forgot' && <>Remembered it? <button type="button" className="am-link" onClick={() => switchTo('sign_in')}>Back to sign in</button></>}
                        </div>
                    </>
                )}

                <p className="am-legal">
                    By continuing, you agree to our{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </p>
            </div>
        </div>
    );
};

export default AuthModal;
